/**
 * Storage Adapter
 * Abstracts key-value storage across platforms
 */

import type { Platform } from './platform';

export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
}

class WebStorageAdapter implements StorageAdapter {
  getItem(key: string): string | null {
    return localStorage.getItem(key);
  }

  setItem(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  removeItem(key: string): void {
    localStorage.removeItem(key);
  }

  clear(): void {
    localStorage.clear();
  }
}

// Electron uses the same localStorage in Chromium renderer
class ElectronStorageAdapter extends WebStorageAdapter {}

// Capacitor uses localStorage in WebView — works the same
// Can be replaced with @capacitor/preferences for secure storage later
class CapacitorStorageAdapter extends WebStorageAdapter {}

let storageAdapterInstance: StorageAdapter | null = null;

export function createStorageAdapter(platform: Platform): StorageAdapter {
  if (storageAdapterInstance) return storageAdapterInstance;

  switch (platform) {
    case 'electron':
      storageAdapterInstance = new ElectronStorageAdapter();
      break;
    case 'capacitor':
      storageAdapterInstance = new CapacitorStorageAdapter();
      break;
    case 'pwa':
    default:
      storageAdapterInstance = new WebStorageAdapter();
      break;
  }

  return storageAdapterInstance;
}

export function getStorageAdapter(): StorageAdapter {
  if (!storageAdapterInstance) {
    // Fallback to web storage — needed because Zustand persist evaluates
    // createJSONStorage at module import time, before initializePlatform() runs.
    storageAdapterInstance = new WebStorageAdapter();
  }
  return storageAdapterInstance;
}
